/**
 * Quotes & Orders domain types.
 *
 * Lifecycle:
 *   Quote (draft → sent → accepted/rejected) ─┐
 *                                              ├─→  Order (received → engineered → scheduled
 *   PO from client → Order directly  ─────────┘     → in_production → qc → shipped → delivered)
 *
 * Both share LineItem structure. An Order can be created from an
 * accepted Quote (one-click conversion) or entered fresh from a PO.
 */

// ─────────── Quotes ───────────

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted';

export const QUOTE_STATUSES: QuoteStatus[] = [
  'draft', 'sent', 'accepted', 'rejected', 'expired', 'converted',
];

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft:     'Draft',
  sent:      'Sent',
  accepted:  'Accepted',
  rejected:  'Rejected',
  expired:   'Expired',
  converted: 'Converted',
};

// ─────────── Orders ───────────

export type OrderStatus =
  | 'received'
  | 'engineered'
  | 'scheduled'
  | 'in_production'
  | 'qc'
  | 'shipped'
  | 'delivered'
  | 'on_hold'
  | 'cancelled';

export const ORDER_STATUSES: OrderStatus[] = [
  'received', 'engineered', 'scheduled', 'in_production',
  'qc', 'shipped', 'delivered', 'on_hold', 'cancelled',
];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  received:      'Received',
  engineered:    'Engineered',
  scheduled:     'Scheduled',
  in_production: 'In Production',
  qc:            'QC',
  shipped:       'Shipped',
  delivered:     'Delivered',
  on_hold:       'On Hold',
  cancelled:     'Cancelled',
};

/** Stages that count as "active" — exclude terminal and on_hold */
export const ACTIVE_ORDER_STATUSES: OrderStatus[] = [
  'received', 'engineered', 'scheduled', 'in_production', 'qc', 'shipped',
];

/** Linear progression for the visual progress bar on detail pages */
export const ORDER_PROGRESSION: OrderStatus[] = [
  'received', 'engineered', 'scheduled', 'in_production', 'qc', 'shipped', 'delivered',
];

// ─────────── Line items ───────────

export type LineItemKind = 'panel' | 'door' | 'jamb' | 'finish' | 'veneer' | 'service' | 'other';

export const LINE_ITEM_KIND_LABELS: Record<LineItemKind, string> = {
  panel:   'Panel',
  door:    'Door',
  jamb:    'Jamb',
  finish:  'Finish · UV',
  veneer:  'Veneer',
  service: 'Service',
  other:   'Other',
};

export type LineItem = {
  id: string;            // client-generated uuid for stable React keys
  kind: LineItemKind;
  description: string;
  sku?: string;
  veneerSpecies?: string;
  finish?: string;
  dimensions?: string;   // free-form: "48 x 96 x 3/4"
  quantity: number;
  unit?: string;         // 'ea', 'sf', 'lf'
  unitPrice: number;
  unitCost?: number;     // for margin calculation (write roles only)
  taxable?: boolean;
  notes?: string;
};

// ─────────── Common totals ───────────

export type Totals = {
  subtotal: number;
  discount: number;
  taxable: number;
  taxRate: number;       // 0–1
  tax: number;
  shipping: number;
  total: number;
  cost?: number;         // sum of (unitCost × qty)
  margin?: number;       // (total − cost) / total
};

// ─────────── Documents ───────────

export type Quote = {
  id: string;
  number: string;        // 'Q-2604' — auto-issued
  status: QuoteStatus;

  clientId: string;
  clientName?: string;   // denormalized for list views
  contactId?: string;
  projectName?: string;

  issueDate: string;      // ISO
  expirationDate?: string;
  acceptedDate?: string;
  convertedOrderId?: string;

  lineItems: LineItem[];

  discountPct?: number;   // 0–1
  taxRatePct?: number;    // 0–1
  shipping?: number;
  notes?: string;
  internalNotes?: string;
  termsAndConditions?: string;

  totals: Totals;

  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  sentAt?: string;
};

export type Order = {
  id: string;
  number: string;        // 'WO-2604'
  status: OrderStatus;

  clientId: string;
  clientName?: string;
  contactId?: string;
  projectName?: string;

  poNumber?: string;     // client's PO ref
  sourceQuoteId?: string;

  receivedDate: string;
  promisedDate?: string;
  shippedDate?: string;
  deliveredDate?: string;

  shippingAddress?: {
    street1?: string; street2?: string; city?: string;
    state?: string; postalCode?: string; country?: string;
    contactName?: string; contactPhone?: string;
  };

  lineItems: LineItem[];

  discountPct?: number;
  taxRatePct?: number;
  shipping?: number;
  notes?: string;
  internalNotes?: string;

  totals: Totals;

  /** Production milestones — set as the order moves through the floor */
  milestones?: {
    engineeredAt?: string;
    scheduledAt?: string;
    productionStartedAt?: string;
    qcAt?: string;
    shippedAt?: string;
    deliveredAt?: string;
  };

  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
};

// ─────────── Calculations ───────────

/**
 * Compute totals from line items + meta. Pure function — used in both
 * the form (live preview) and the backend (canonical write).
 */
export function computeTotals(
  lineItems: LineItem[],
  opts: { discountPct?: number; taxRatePct?: number; shipping?: number } = {},
): Totals {
  const subtotal = lineItems.reduce((s, li) => s + li.quantity * li.unitPrice, 0);
  const discount = subtotal * (opts.discountPct ?? 0);
  const afterDiscount = subtotal - discount;

  const taxable = lineItems
    .filter((li) => li.taxable !== false)
    .reduce((s, li) => s + li.quantity * li.unitPrice, 0)
    * (1 - (opts.discountPct ?? 0));

  const taxRate = opts.taxRatePct ?? 0;
  const tax = taxable * taxRate;
  const shipping = opts.shipping ?? 0;
  const total = afterDiscount + tax + shipping;

  const cost = lineItems.some((li) => li.unitCost !== undefined)
    ? lineItems.reduce((s, li) => s + li.quantity * (li.unitCost ?? 0), 0)
    : undefined;

  const margin = cost !== undefined && total > 0
    ? (total - cost) / total
    : undefined;

  return { subtotal, discount, taxable, taxRate, tax, shipping, total, cost, margin };
}
